import {
  Autocomplete,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import iso3311a2 from 'iso-3166-1-alpha-2';
import { makeStyles } from '@mui/styles';
import { COLOR_GRAY_70 } from 'styles/variables/colors';
import StripeElement from '../StripleElement';
import { IbanElement } from '@stripe/react-stripe-js';
import { europeanCountries, getCode } from 'utils/countries';
import { useEffect, useState } from 'react';
import { PaymentMethodEnum } from 'enums';
import { useCheckoutContext } from 'contexts/CheckoutContext';
import { PaymentError } from '../PaymentError';

const useStyles = makeStyles((theme) => ({
  text: {
    color: COLOR_GRAY_70,
    marginBottom: theme.spacing(2),
  },
  removePaddingLeft: {
    paddingLeft: '0 !important',
  },
}));

const initialFormData = {
  fields: {
    iban: { value: '', required: true },
    name: { value: '', required: true },
    email: { value: '', required: true },
    address: { value: '', required: true },
    city: { value: '', required: true },
    postalCode: { value: '', required: true },
    country: { value: '', required: true },
  },
};

export default function SEPADebitContent() {
  const classes = useStyles();
  const { data, actions } = useCheckoutContext();
  const [formFieldsWithError, setFormFieldsWithError] = useState([]);
  const [formData, setFormData] = useState({
    ...initialFormData,
    fields: {
      ...initialFormData.fields,
      name: { value: data.user.name || '', required: true },
      email: { value: data.user.email || '' },
      country: {
        value: data.user.countryCode ? iso3311a2.getCountry(data.user.countryCode) : '',
        required: true,
      },
    },
  });

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      fields: {
        ...formData.fields,
        [field]: { value, required: formData.fields[field].required },
      },
    });

    if (formFieldsWithError.includes(field)) {
      setFormFieldsWithError(formFieldsWithError.filter((item) => item !== field));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const isValid = validate();

    if (!isValid) {
      return;
    }

    actions.beforeSubmit(formData);

    try {
      const { data: setupIntent } = await data.paymentService.createStripeSetupIntent({
        paymentMethod: PaymentMethodEnum.SEPA_DEBIT,
      });
      const clientSecret = setupIntent.client_secret;
      const iban = data.stripeElements.getElement(IbanElement);
      const result = await data.stripe.confirmSepaDebitSetup(clientSecret, {
        payment_method: {
          sepa_debit: iban,
          billing_details: {
            name,
            email,
            address: {
              line1: formData.fields.address.value,
              city: formData.fields.city.value,
              state: formData.fields.state.value,
              postal_code: formData.fields.postalCode.value,
              country: getCode(formData.fields.country.value),
            },
          },
        },
      });

      if (result.error) {
        throw new PaymentError(result.error.message);
      }

      const paymentMethodId = result.setupIntent.payment_method;
      const response = await data.paymentService.stripe({
        plan,
        billingInterval,
        teamSize,
        paymentMethodId,
        paymentMethodType: PaymentMethodEnum.SEPA_DEBIT,
        couponCode: data.coupon?.code,
      });

      actions.afterSubmit({
        success: true,
        data: response.data,
        paymentMethod: PaymentMethodEnum.SEPA_DEBIT,
      });
    } catch (e) {
      actions.afterSubmit({ success: false, error: e });
    }
  };

  const validate = () => {
    const fields = Object.keys(formData.fields);
    const errorFields = [];

    for (const field of fields) {
      if (formData.fields[field].required && !formData.fields[field].value) {
        errorFields.push(field);
      }
    }

    if (errorFields.length > 0) {
      setFormFieldsWithError(errorFields);
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (formFieldsWithError.length > 0 && !data.hasFormError) {
      actions.setHasFormError(true);
    } else if (formFieldsWithError.length === 0 && data.hasFormError) {
      actions.setHasFormError(false);
    }
  }, [formFieldsWithError]);

  return (
    <form onSubmit={handleSubmit}>
      <Grid container={true} spacing={1}>
        <Grid item={true} xs={12} sm={12}>
          <Typography className={classes.text}>
            SEPA Direct Debit is a reusable and delayed notification payment method. Which means it
            takes an average of 5 working days for your payment to be confirmed and your
            subscription to be activated (the deadline for this confirmation is 14 working days).
          </Typography>

          <StripeElement
            element={IbanElement}
            error={formFieldsWithError.includes('iban')}
            options={{ supportedCountries: ['SEPA'] }}
            onChange={(e) => handleChange('iban', e.complete)}
          />
          {formFieldsWithError.includes('iban') && (
            <FormHelperText variant="outlined" error>
              Please fill this field correctly
            </FormHelperText>
          )}

          <FormControl variant="standard" fullWidth={true} margin="dense">
            <TextField
              variant="outlined"
              error={formFieldsWithError.includes('name')}
              helperText={
                formFieldsWithError.includes('name') && 'Please fill this field correctly'
              }
              label="Name"
              name="name"
              value={formData.fields.name.value}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </FormControl>

          <FormControl variant="standard" fullWidth={true} margin="dense">
            <TextField
              variant="outlined"
              error={formFieldsWithError.includes('email')}
              helperText={
                formFieldsWithError.includes('email') && 'Please fill this field correctly'
              }
              label="Email"
              name="email"
              type="email"
              value={formData.fields.email.value}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item={true} xs={12}>
          <FormControl variant="standard" fullWidth={true} margin="dense">
            <TextField
              variant="outlined"
              error={formFieldsWithError.includes('address')}
              helperText={
                formFieldsWithError.includes('address') && 'Please fill this field correctly'
              }
              label="Address"
              name="address"
              value={formData.fields.address.value}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item={true} xs={6} sm={6}>
          <FormControl variant="standard" fullWidth={true} margin="dense">
            <TextField
              variant="outlined"
              error={formFieldsWithError.includes('city')}
              helperText={
                formFieldsWithError.includes('city') && 'Please fill this field correctly'
              }
              label="City"
              name="city"
              value={formData.fields.city.value}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item={true} xs={6} sm={3}>
          <FormControl variant="standard" fullWidth={true} margin="dense">
            <TextField
              variant="outlined"
              error={formFieldsWithError.includes('postalCode')}
              helperText={
                formFieldsWithError.includes('postalCode') && 'Please fill this field correctly'
              }
              label="Postal Code"
              name="postalCode"
              value={formData.fields.postalCode.value}
              onChange={(e) => handleChange('postalCode', e.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item={true} xs={6} sm={3}>
          <FormControl variant="standard" fullWidth={true} margin="dense">
            <Autocomplete
              options={['', ...europeanCountries]}
              value={formData.fields.country.value}
              onChange={(_, value) => handleChange('country', value || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  error={formFieldsWithError.includes('country')}
                  helperText={
                    formFieldsWithError.includes('country') && 'Please fill this field correctly'
                  }
                  label="Country"
                  name="country"
                />
              )}
            />
          </FormControl>
        </Grid>

        <input type="submit" style={{ display: 'none' }} ref={data.formRef} />
      </Grid>
    </form>
  );
}
